// The module extension contract (see src/modules/README.md). Each module folder exports
//   export const module: WebModule = { routes: [], entityOverrides: {} }
// and src/modules/index.ts merges them. Kept free of runtime React/router imports so it is unit-testable.
import type { AnyRoute } from '@tanstack/react-router'
import type { ComponentType } from 'react'
import type { Viewer } from './permissions'
import type { EntitySchema, NavGroup, RecordRow } from './schema'

export interface EntityListPageProps {
  entity: EntitySchema
}

export interface EntityDetailPageProps {
  entity: EntitySchema
  /** Record id or key from the URL. */
  id: string
}

export interface DetailTabProps {
  entity: EntitySchema
  record: RecordRow
}

export type DefaultTabId = 'overview' | 'links' | 'history' | 'notes'

export interface DetailTab {
  /** Stable id, used in the URL (?tab=<id>). Must not collide with the default tab ids. */
  id: string
  label: string
  /** Position among tabs. Defaults: overview 10, links 20, history 80, notes 90. Module tabs default to 50. */
  order?: number
  component: ComponentType<DetailTabProps>
  /** Hide the tab for some records (e.g. only for custom controls). */
  visible?: (ctx: { entity: EntitySchema; record: RecordRow }) => boolean
}

export interface EntityOverride {
  /** Replaces the generic list page at /<plural>. */
  ListPage?: ComponentType<EntityListPageProps>
  /** Replaces the generic detail page at /<plural>/<id>. */
  DetailPage?: ComponentType<EntityDetailPageProps>
  /** Extra tabs on the generic detail page. */
  detailTabs?: DetailTab[]
  /** Default tabs to hide on the generic detail page (e.g. 'links' when a module tab shows mappings better). */
  hideDefaultTabs?: DefaultTabId[]
}

export interface NavEntry {
  /** Unique key. Reusing a built-in key (e.g. 'trust', 'dashboard', 'controls') replaces that entry. */
  key: string
  label: string
  to: string
  group: NavGroup
  order: number
  /** Built-in icon name (see app/nav-icons.ts) or a component. */
  icon?: string | ComponentType<{ className?: string }>
  /** Entity whose read permission decides visibility. */
  entity?: string
  /** Module whose read permission decides visibility (when there is no entity). */
  module?: string
  /** Custom visibility rule; wins over entity/module. */
  visible?: (viewer: Viewer) => boolean
}

export interface WebModule {
  /** Extra TanStack routes. Create them with getParentRoute: () => appRoute (from src/app/route-base). */
  routes: AnyRoute[]
  /** Keyed by entity name (e.g. 'control', 'evidence_item'), not plural. */
  entityOverrides: Record<string, EntityOverride>
  /** Extra or replacement sidebar entries. */
  nav?: NavEntry[]
}

export interface MergedModules {
  routes: AnyRoute[]
  entityOverrides: Record<string, EntityOverride>
  nav: NavEntry[]
  /** Conflicts found while merging (two modules replacing the same page, duplicate route paths, tab ids). */
  problems: string[]
}

const DEFAULT_TABS: DefaultTabId[] = ['overview', 'links', 'history', 'notes']

function routePath(r: AnyRoute): string | undefined {
  const opts = (r as unknown as { options?: { path?: string; id?: string } }).options
  return opts?.path ?? opts?.id
}

/** Merges module contributions in name order. On conflict the first module wins and the conflict is reported. */
export function mergeModules(mods: Record<string, WebModule | undefined>): MergedModules {
  const routes: AnyRoute[] = []
  const entityOverrides: Record<string, EntityOverride> = {}
  const owners: Record<string, string> = {}
  const nav: NavEntry[] = []
  const problems: string[] = []
  const seenPaths = new Map<string, string>()
  const seenNav = new Map<string, string>()

  for (const name of Object.keys(mods).sort()) {
    const m = mods[name]
    if (!m) {
      problems.push(`module ${name} does not export "module"`)
      continue
    }
    for (const r of m.routes ?? []) {
      const p = routePath(r)
      if (p && seenPaths.has(p)) {
        problems.push(
          `route ${p} is defined by both ${seenPaths.get(p)} and ${name}; keeping ${seenPaths.get(p)}`,
        )
        continue
      }
      if (p) seenPaths.set(p, name)
      routes.push(r)
    }
    for (const [entity, o] of Object.entries(m.entityOverrides ?? {})) {
      const cur: EntityOverride = entityOverrides[entity] ?? {}
      entityOverrides[entity] = cur
      for (const slot of ['ListPage', 'DetailPage'] as const) {
        if (!o[slot]) continue
        const key = `${entity}.${slot}`
        if (cur[slot]) {
          problems.push(`${key} is overridden by both ${owners[key]} and ${name}; keeping ${owners[key]}`)
          continue
        }
        cur[slot] = o[slot] as never
        owners[key] = name
      }
      for (const tab of o.detailTabs ?? []) {
        if ((DEFAULT_TABS as string[]).includes(tab.id)) {
          problems.push(`${entity} tab id "${tab.id}" from ${name} collides with a default tab`)
          continue
        }
        if (cur.detailTabs?.some((t) => t.id === tab.id)) {
          problems.push(`${entity} tab id "${tab.id}" is defined twice (${name})`)
          continue
        }
        cur.detailTabs = [...(cur.detailTabs ?? []), tab]
      }
      if (o.hideDefaultTabs?.length) {
        cur.hideDefaultTabs = [...new Set([...(cur.hideDefaultTabs ?? []), ...o.hideDefaultTabs])]
      }
    }
    for (const n of m.nav ?? []) {
      if (seenNav.has(n.key)) {
        problems.push(
          `nav entry "${n.key}" is defined by both ${seenNav.get(n.key)} and ${name}; keeping ${seenNav.get(n.key)}`,
        )
        continue
      }
      seenNav.set(n.key, name)
      nav.push(n)
    }
  }
  return { routes, entityOverrides, nav, problems }
}

/** Tabs for a detail page: default tabs minus hidden ones, plus module tabs, sorted by order. */
export function detailTabsFor(
  override: EntityOverride | undefined,
  ctx: { entity: EntitySchema; record: RecordRow },
  defaults: Array<{ id: DefaultTabId; label: string; order: number }>,
): Array<{ id: string; label: string; order: number; custom?: DetailTab }> {
  const hidden = new Set(override?.hideDefaultTabs ?? [])
  const out: Array<{ id: string; label: string; order: number; custom?: DetailTab }> = defaults.filter(
    (t) => !hidden.has(t.id),
  )
  for (const t of override?.detailTabs ?? []) {
    if (t.visible && !t.visible(ctx)) continue
    out.push({ id: t.id, label: t.label, order: t.order ?? 50, custom: t })
  }
  return out.sort((a, b) => a.order - b.order)
}
